import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ChevronDown } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";
import { APP_LINKS, CLUB, IMAGES, imgSrc, imgSrcset } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section ref={ref} className="hero-vignette grain relative flex min-h-[100svh] items-end overflow-hidden bg-night" data-testid="hero-section">
      <motion.div className="absolute inset-0 will-change-transform" style={{ y: bgY }}>
        <motion.img
          src={imgSrc("heroRider", 1280)}
          srcSet={imgSrcset("heroRider", [480, 800, 1200, 1600, 1920])}
          sizes="100vw"
          width={1920}
          height={1192}
          alt="Motards entrepreneurs en sortie au coucher du soleil"
          className="h-[115%] w-full object-cover"
          loading="eager"
          fetchPriority="high"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: EASE }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-night/85 via-night/40 to-transparent" aria-hidden="true" />

      <motion.div style={{ opacity: contentOpacity, y: contentY }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-20 pt-36 sm:px-6 lg:px-8 lg:pb-28">
        <motion.div
          initial={{ opacity: 0, x: -18 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="h-px w-12 bg-gold" />
          <span className="eyebrow">Club privé d'entrepreneurs motards</span>
        </motion.div>

        <h1 className="font-heading font-extrabold uppercase leading-[0.95] tracking-tight text-foreground">
          <MaskedLine delay={0.2} className="text-[9.5vw] sm:text-[10.5vw] lg:text-[7.5rem]">Motards</MaskedLine>
          <MaskedLine delay={0.32} className="text-[9.5vw] sm:text-[10.5vw] lg:text-[7.5rem]">
            <span className="text-transparent [-webkit-text-stroke:1.5px_rgba(212,175,55,0.75)]">Business</span>
          </MaskedLine>
          <MaskedLine delay={0.44} className="text-[9.5vw] sm:text-[10.5vw] lg:text-[7.5rem]">
            <span className="text-gold-grad">Club 84</span>
          </MaskedLine>
          <MaskedLine delay={0.5} className="mt-4">
            <span className="block font-sans text-sm font-medium normal-case tracking-normal text-gold-light/90 sm:text-base">
              Le business club des entrepreneurs motards du Vaucluse — Avignon, Gard, Sud-Est
            </span>
          </MaskedLine>
        </h1>

        <MaskedLine delay={0.6} className="mt-6">
          <span className="font-serif text-2xl italic text-gold-light sm:text-3xl">{CLUB.baseline}</span>
        </MaskedLine>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.72, ease: EASE }}
          className="mt-5 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg"
        >
          {CLUB.heroText}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
          className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <a
            href={APP_LINKS.joinClub}
            data-testid="hero-join-button"
            className="btn-sheen inline-flex items-center justify-center rounded-full bg-gradient-to-r from-gold-deep via-gold to-gold-hover px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] text-night shadow-[0_8px_40px_rgba(212,175,55,0.4)] transition-transform duration-300 hover:scale-[1.04]"
          >
            Rejoindre le club
          </a>
          <a
            href="#application"
            data-testid="hero-discover-app-button"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById("application");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            className="group inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] text-gold transition-all duration-300 hover:border-gold hover:bg-gold/10"
          >
            Découvrir l'application
            <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.05 }}
          className="mt-10 inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-night/50 px-5 py-2.5 backdrop-blur-md"
          data-testid="hero-regions-badge"
        >
          <MapPin className="h-4 w-4 text-gold" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/85">{CLUB.regions}</span>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-6 right-6 z-10 hidden flex-col items-center gap-2 md:flex"
        aria-hidden="true"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Scroll</span>
        <span className="relative flex h-9 w-5 items-start justify-center rounded-full border border-gold/40 p-1.5">
          <span className="h-1.5 w-1 animate-scrollcue rounded-full bg-gold" />
        </span>
        <ChevronDown className="h-3 w-3 text-gold/60" />
      </motion.div>
    </section>
  );
}
